// CSA page content — sourced from worldfarmers.org/csa, /csa/csa-faqs,
// /csa/snap-hip-for-csa, /csa/csa-pay-by-check and /product/world-farmers-csa-2026.
// Pricing, dates, pickup locations, and FAQ copy are faithful to the source.
// CHECKOUT IS A DUMMY FLOW — no payment is processed; the client has not yet
// selected a payment provider.

export const CSA_HERO = {
  eyebrow: "World Farmers CSA 2026",
  title: "A season of fresh, local vegetables \u2014 grown by the farmers at the Flats",
  lede:
    "Pick-up days every Thursday for 16 weeks, July 9th \u2013 October 22nd, 2026. Choose a share, pick a location near you, and try a new cultural crop every week.",
  image: "/images/wf/vlai/vlai-3514.jpg",
  imageAlt: "Gloved hands cradle a bundle of freshly harvested greens.",
};

export const CSA_SEASON = {
  cadence: "Every Thursday",
  weeks: "16 weeks",
  dates: "July 9 \u2013 October 22, 2026",
  note:
    "You can still sign up after we start our CSA season! If you sign up late, we\u2019ll confirm the earliest week we can accommodate and your prorated membership cost.",
};

export const CSA_CONTACT = {
  name: "Our CSA Team",
  email: "csa@worldfarmers.org",
  phone: "(978) 706-7935",
  phoneHref: "tel:+19787067935",
  hostNote:
    "If your place of business is interested in becoming a CSA pick-up location, please call (978) 706-7935 and ask for our CSA Team.",
};

export const CSA_SHARE_OPTIONS = [
  {
    id: "full",
    name: "Full Share",
    feeds: "Feeds approximately 3\u20134 people",
    items: 9,
    description:
      "Includes a selection of 9 seasonal leafy greens, root vegetables, fruiting vegetables, herbs and a featured cultural crop.",
    price: 600,
    priceLabel: "$600",
    seasonLabel: "16-week season",
    installments: true,
    snapMonthly: 80,
    snapNote:
      "If you receive $80 or more in SNAP dollars per month, this selection will not touch your SNAP dollars \u2014 it will pull from your qualifying HIP benefits. If you receive less than $80 per month, we recommend the Partial Share.",
    featured: true,
    image: "/images/wf/farm-035.jpg",
    imageAlt: "A smiling farmer holding a freshly harvested bundle of leafy greens in a sunlit field.",
  },
  {
    id: "partial",
    name: "Partial Share",
    feeds: "Feeds approximately 1\u20132 people",
    items: 6,
    description:
      "Includes a selection of 6 seasonal leafy greens, root vegetables, fruiting vegetables, herbs and a featured cultural crop.",
    price: 480,
    priceLabel: "$480",
    seasonLabel: "16-week season",
    installments: true,
    snapMonthly: 60,
    snapNote:
      "If you receive $60 or more in SNAP dollars per month, this selection will not touch your SNAP dollars \u2014 it will pull from your qualifying HIP benefits. If you receive less than $60 per month, the remainder will be deducted from your SNAP dollars.",
    featured: false,
    image: "/images/wf/farmer5.jpg",
    imageAlt: "A smiling farmer standing in a field holding a bundle of harvested produce.",
  },
];

export const CSA_PICKUP_LOCATIONS = [
  { id: "lancaster", name: "World Farmers Office", address: "769 Main Street, Lancaster, MA", town: "Lancaster" },
  { id: "concord", name: "The Thoreau Club", address: "275 Forest Ridge Rd, Concord, MA", town: "Concord" },
  { id: "natick", name: "Longfellow Health Club \u00b7 Natick", address: "203 Oak St, Natick, MA", town: "Natick" },
  { id: "wayland", name: "Longfellow Tennis & Health Club \u00b7 Wayland", address: "524 Boston Post Rd, Wayland, MA", town: "Wayland" },
];

export const CSA_SEASONS = [
  {
    id: "early",
    label: "Early in season",
    crops: ["Lettuce mix", "Arugula", "Garlic scapes", "Basil", "Summer squash", "Red radish", "Snap peas", "Scallions"],
    image: "/images/wf/vlai/vlai-3475.jpg",
    imageAlt: "Young seedlings pushing up through dark, freshly turned soil.",
  },
  {
    id: "peak",
    label: "Peak season",
    crops: ["Green bell peppers", "Cilantro", "Tomatoes", "Swiss chard", "Blueberries", "Lettuce", "Celery", "Collard greens"],
    image: "/images/wf/vlai/vlai-3735.jpg",
    imageAlt: "Pepper plants heavy with ripening fruit.",
  },
  {
    id: "fall",
    label: "Fall season",
    crops: ["Beets", "Parsley", "Kale", "Onion", "Green beans", "Eggplant", "Winter squash", "Carrots"],
    image: "/images/wf/farm-3634.jpg",
    imageAlt: "A side-view profile of a farmer with a sun hat kneeling to tend to crops.",
  },
];

export const CSA_CULTURAL_NOTE =
  "In addition to a selection of mainstream crops, you\u2019ll receive a cultural crop to try at home, complete with a crop description and recipe card! Some of these crops may include amaranth, lalu, jilo, chipilin, tatsoi, sweet potato leaves, or pumpkin vines, just to name a few.";

export const CSA_PAYMENT_METHODS = [
  {
    id: "card",
    label: "Pay by card",
    short: "Online, includes processing fee",
    detail: "Pay online with a credit or debit card. Includes a processing fee. Choose to pay in full or in two installments.",
  },
  {
    id: "check",
    label: "Pay by check",
    short: "Mail a check, avoids processing fee",
    detail:
      "Fill out your share details so we know to expect your check, then mail it to World Farmers, P.O. Box 112, Lancaster, MA 01523. We\u2019ll email a confirmation when it arrives.",
    mailTo: ["Check payable to: World Farmers", "World Farmers", "P.O. Box 112", "Lancaster, MA 01523"],
  },
  {
    id: "snap",
    label: "SNAP & HIP",
    short: "Monthly Auto-SNAP through DTA",
    detail:
      "We cannot take SNAP payments online. Share your details and we\u2019ll follow up to complete the DTA contract \u2014 a monthly charge on the day you receive your SNAP dollars, for 4 months / 16 weeks. HIP benefits are applied first.",
  },
];

export const CSA_SNAP_INFO = {
  title: "Are you a SNAP recipient?",
  intro:
    "World Farmers is excited to offer Supplemental Nutrition Assistance Program (SNAP) as a payment option for our CSA. And, your payment will qualify for Massachusetts Healthy Incentives Program (HIP) benefits.",
  points: [
    "The SNAP/HIP CSA operates through a contract with DTA. Once you sign up, we will reach out to you to fill out the contract.",
    "The contract will operate for 4 months / 16 weeks, and will deduct a monthly charge on the day you receive your SNAP dollars.",
    "The contract will select all HIP dollars/benefits first, and any remaining costs will take from your SNAP dollars, so be sure to select the share option that works with your SNAP budget.",
    "We are happy to accept SNAP sign-ups at any point in the season; however, we can only sign you up in month-long (4 week) increments because of the way Auto-SNAP works with DTA.",
  ],
  faqs: [
    { q: "What is SNAP?", a: "The Supplemental Nutrition Assistance Program (SNAP) is federal assistance that ensures needy families have access to healthy food options." },
    { q: "What is HIP?", a: "HIP is the Healthy Incentives Program that promotes SNAP clients to purchase more locally grown fruits and vegetables from farmers\u2019 markets, mobile markets, and CSA farm shares. Once you are enrolled in SNAP, you automatically qualify for HIP benefits with no additional steps \u2014 you just have to spend SNAP dollars at a certified HIP vendor. World Farmers is a HIP vendor, so your HIP benefits will pay for your CSA share." },
    { q: "How do I use my SNAP benefits to purchase my CSA?", a: "You will pay for your CSA with what\u2019s called Auto-SNAP. Each month, directly after receiving your SNAP dollars, the cost of your CSA will be withdrawn from your account on that same day." },
    { q: "So I\u2019ll spend all of my SNAP dollars?", a: "Not necessarily! We have two share size options for you to choose from. As long as you receive at least that amount in SNAP dollars, then your share will not use any SNAP dollars, and will instead pull from your HIP benefits, which can only be spent with certified vendors." },
    { q: "So how do I sign up?", a: "If you are planning to pay for your World Farmers CSA with SNAP/EBT, fill out the SNAP & HIP form in the sign-up flow. We cannot take SNAP payments online, so we will follow up with you to get you signed up." },
  ],
};

export const CSA_FAQS = [
  {
    q: "What is a CSA?",
    a: "CSA stands for \u201CCommunity Supported Agriculture,\u201D and a CSA \u201Cshare\u201D or \u201Csubscription\u201D offers a way for you to support local farming while receiving fresh-from-the-farm weekly seasonal crops from the bounty of farmers\u2019 harvests. By subscribing to our CSA you become an instrumental part of supporting local farmers\u2019 businesses. Your share provides a guaranteed market, paid upfront to assist with the season\u2019s production needs.",
  },
  {
    q: "How do I sign up?",
    a: "It\u2019s easy! Use the sign-up steps on this page: choose a share, pick a pick-up location, and select how you\u2019d like to pay. We have pick-up locations across eastern Massachusetts for your convenience.",
  },
  {
    q: "Why World Farmers\u2019 CSA?",
    a: "World Farmers operates Flats Mentor Farm in Lancaster, MA, which provides access to land and markets to over 260 immigrant and refugee farmers from over 25 countries. Each farmer\u2019s crop plan reflects the food of their home, and each week we will feature a new cultural crop for you to try at home, complete with a recipe card for inspiration.",
  },
  {
    q: "What can I expect in my weekly share?",
    a: "A Full Share feeds ~4 people and includes a selection of 9 seasonal leafy greens, root vegetables, squashes, and herbs. A Partial Share feeds ~2 people, and includes a selection of 6 seasonal leafy greens, root vegetables, squashes, and herbs. Both share types will include fruits or other goodies from neighboring farms from time to time; and will feature 1 cultural crop with a recipe suggestion!",
  },
  {
    q: "How is this share different from other CSA programs?",
    a: "Each farmer\u2019s produce grown at Flats Mentor Farm is their own and often reflects their culture and ethnic diversity. The featured ethnic crop will be one that is important to the farmers\u2019 food heritage, and may be hard to find in the grocery store. Some of these crops may include muchicha/amaranth, lalu, jilo, chipilin, water spinach, tatsoi, sweet potato leaves, or pumpkin vines, to name a few. We also use the CSA market opportunity to support farmers in-training, who are still learning how to estimate quantities for buyers and may have surplus. By supporting our CSA, you are directly supporting beginning farmer training.",
  },
  {
    q: "What are your payment options?",
    a: "We accept checks, online card payments, and our CSA is SNAP and HIP accessible. World Farmers is doing what we can to increase access to local, healthful food for all.",
  },
  {
    q: "Can I still sign up after the season starts?",
    a: "Yes! If you miss the CSA start date, we are still happy to accept signups throughout the season. If you sign up late, please email or call us to connect, and we will confirm the earliest week we can accommodate, and confirm your prorated membership cost. Contact our CSA Team at csa@worldfarmers.org or (978) 706-7935.",
  },
];
